import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../api/shop';
import { usePageTitle } from '../a11y/usePageTitle';
import { brand } from '../brand/brand';
import { DealCard } from '../components/DealCard';
import type { Deal, MarketplaceCategory } from '../api/types';

/*
 * Direction B homepage: hero, category shortcuts, featured deals, how it works,
 * merchant CTA.
 *
 * THE HERO STATS ARE NOT IN THIS BUILD.
 *
 * The design shows "2,400+ local businesses", "4.8 average rating" and "$1.4M
 * saved this year". Every one of those is a factual claim about the business,
 * none is available from any endpoint, and two of them ("4.8", "$1.4M") would be
 * false on a platform with one seeded restaurant. Printing them would be
 * inventing evidence on the page whose entire job is persuading someone to prepay
 * a stranger. The refund promise IS in the terms, so that tile is real; the others
 * appear only once something can supply them.
 */

/*
 * The hero photograph, served from web/shop/public/media/ — see the README there.
 *
 * Referenced by a fixed path rather than imported, so that dropping a file in
 * replaces it with no rebuild and no code change. The trade is that a MISSING file
 * is a runtime 404 instead of a build error, which is why the <img> below falls
 * back to the striped placeholder rather than leaving a broken-image icon on the
 * most important screen on the site.
 */
const HERO_IMAGE = '/media/hero.jpg';

export function Home() {
  const [params, setParams] = useSearchParams();

  const [deals, setDeals] = useState<Deal[] | null>(null);

  /* THE CATEGORY LIST COMES FROM THE DATA. It used to be seven names copied from
     the design handoff, filtered client-side against Deal.category — two
     vocabularies that were never reconciled, so the buttons matched nothing and
     the "filter" was the browser hiding rows from one truncated page.

     The server returns only categories that have live deals, so a button can
     never lead to an empty result. */
  const [categories, setCategories] = useState<MarketplaceCategory[]>([]);
  const [heroFailed, setHeroFailed] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const query = params.get('q') ?? '';
  const category = params.get('category');

  usePageTitle(query === '' ? null : `Search: ${query}`);

  useEffect(() => {
    // Once, not per query: the set of categories does not change with a search
    // term, and re-fetching it on every keystroke would make the row flicker.
    api.dealCategories().then(setCategories).catch(() => setCategories([]));
  }, []);

  useEffect(() => {
    let cancelled = false;
    setDeals(null);
    setError(null);

    // BOTH parameters go to the server now. The API has always accepted
    // `category`; the shop simply never sent it.
    api.deals({
      ...(query === '' ? {} : { search: query }),
      ...(category === null ? {} : { category }),
    })
      .then((result) => { if (!cancelled) setDeals(result); })
      .catch(() => { if (!cancelled) setError('We could not load offers just now. Please try again.'); });

    return () => { cancelled = true; };
  }, [query, category]);

  /* No client-side filtering any more. The server applies the category to the
     WHOLE catalogue; filtering here would only ever have narrowed one page of
     results and called that a category. */
  const shown = deals;

  function setCategory(next: string | null) {
    const p = new URLSearchParams(params);
    if (next === null) p.delete('category'); else p.set('category', next);
    setParams(p, { replace: true });
  }

  return (
    <>
      <section className="hero">
        <div className="stack gap-lg">
          <span className="eyebrow">Prepaid credit at local businesses</span>

          <h1>{brand.tagline}</h1>

          <p className="lead">{brand.description}</p>

          <div className="hero-actions">
            <a href="#deals" className="btn cta light">Browse deals</a>
            <Link to="/how-it-works" className="btn cta ghost">How it works</Link>
          </div>

          <div className="stats">
            {/* The one stat that is a fact we control, worded exactly as the terms and the refund process are: on request, unused only. It said "14 days" until 30 Sep 2026 - a window the terms never promised and nothing enforced. */}
            <div className="stat">
              <span className="value">Refundable</span>
              <span className="label">Unused vouchers, on request</span>
            </div>
          </div>
        </div>

        <div className="hero-media">
          {heroFailed
            ? <div className="ph">lifestyle hero photo</div>
            : (
              /* alt="" — decorative. The headline beside it already says what the
                 site is, and describing the photo would make a screen reader read
                 the hero twice. */
              <img
                src={HERO_IMAGE}
                alt=""
                className="hero-photo"
                onError={() => setHeroFailed(true)}
              />
            )}
        </div>
      </section>

      <section className="section" aria-labelledby="cats-h">
        <h2 id="cats-h" className="visually-hidden">Categories</h2>

        <div className="cats">
          <button
            type="button"
            className="cat"
            onClick={() => setCategory(null)}
            aria-pressed={category === null}
          >
            <span className="cat-icon" aria-hidden="true" />
            All deals
          </button>

          {categories.map((c) => (
            <button
              key={c.category}
              type="button"
              className="cat"
              onClick={() => setCategory(c.category)}
              aria-pressed={category === c.category}
            >
              <span className="cat-icon" aria-hidden="true" />
              {c.category}
            </button>
          ))}
        </div>
      </section>

      <section className="section" id="deals">
        <div className="section-head">
          <div>
            <h2>{query === '' ? 'Featured deals' : `Results for “${query}”`}</h2>
            <p className="sub">
              {category === null
                ? 'Handpicked, updated every morning.'
                : `Showing ${category}.`}
            </p>
          </div>
        </div>

        {error !== null && <p className="notice error" role="alert">{error}</p>}

        {/*
          THE RESULT COUNT, ANNOUNCED.
          Searching and filtering both replace the grid in place, and a grid that
          silently changes is invisible to a screen reader — the customer types a
          search, presses enter and hears nothing at all. Visually hidden because
          sighted users can see the grid change.
        */}
        {shown !== null && (
          <p className="visually-hidden" role="status">
            {shown.length === 0
              ? 'No offers match.'
              : `${shown.length} ${shown.length === 1 ? 'offer' : 'offers'}${category === null ? '' : ` in ${category}`}${query === '' ? '' : ` for ${query}`}.`}
          </p>
        )}

        {shown === null && error === null && (
          <>
            <p className="visually-hidden" role="status">Loading offers…</p>
            <div className="grid" aria-hidden="true">
              {[0, 1, 2, 3].map((i) => <div key={i} className="skeleton skeleton-card" />)}
            </div>
          </>
        )}

        {shown !== null && shown.length === 0 && (
          <div className="panel">
            <h3>Nothing here yet</h3>
            <p className="muted" style={{ marginTop: 8 }}>
              {query !== '' || category !== null
                ? 'Try a different search or category.'
                : 'Check back shortly — new offers are added regularly.'}
            </p>
            {(query !== '' || category !== null) && (
              <Link to="/" className="btn secondary" style={{ marginTop: 16 }}>
                Show every deal
              </Link>
            )}
          </div>
        )}

        {shown !== null && shown.length > 0 && (
          <div className="grid">
            {shown.map((deal) => <DealCard key={deal.id} deal={deal} />)}
          </div>
        )}
      </section>

      <section className="section">
        <div className="panel">
          <h2 style={{ marginBottom: 18 }}>How it works</h2>

          <div className="steps">
            <div className="step">
              <div className="num" aria-hidden="true">1</div>
              <h3>Pick a deal</h3>
              <p>Browse vouchers near you and see exactly what the credit is worth.</p>
            </div>

            <div className="step">
              <div className="num" aria-hidden="true">2</div>
              <h3>Pay less than face value</h3>
              <p>Checkout takes seconds. The voucher lands in My vouchers straight away.</p>
            </div>

            <div className="step">
              <div className="num" aria-hidden="true">3</div>
              <h3>Spend it at the counter</h3>
              <p>Show it on your phone. Any remaining balance stays yours until it expires.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="band">
          <div>
            <h2>Own a business? Fill quiet hours.</h2>
            <p>You set the value, the discount and the limits. Listing takes minutes.</p>
          </div>

          <Link to="/support" className="btn cta">Start selling</Link>
        </div>
      </section>
    </>
  );
}
